import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Phone, MapPin, Clock, FileText } from "lucide-react";
import { BRAND_EMAIL, BRAND_PHONE, INSTAGRAM, FACEBOOK } from "@/lib/config";
import { ORG_CONTACT, formatAddressLine } from "@/lib/seo";

export default function Footer() {
  return (
    <footer className="bg-forest text-cream mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <img
            src="/hopsy-logo.webp"
            alt="Hopsy"
            className="h-14 w-auto object-contain logo-light mb-5"
          />
          <p className="tag-line mb-6" style={{ color: "#F3BE4A" }}>Complete Care For Small Pets</p>
          <p className="text-cream/80 text-sm leading-relaxed max-w-md font-light">
            Nutritious, sun-cured hay, premium pellets, and natural bedding —
            crafted with care for rabbits, guinea pigs, hamsters and chinchillas.
            A product of RSA Overseas Pvt Ltd.
          </p>
          <div className="flex items-center gap-5 mt-8">
            <a href={INSTAGRAM} target="_blank" rel="noreferrer" data-testid="footer-ig" className="hover:text-sun transition">
              <Instagram className="w-5 h-5" strokeWidth={1.5} />
            </a>
            <a href={FACEBOOK} target="_blank" rel="noreferrer" data-testid="footer-fb" className="hover:text-sun transition">
              <Facebook className="w-5 h-5" strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="tag-line mb-5" style={{ color: "#F3BE4A" }}>Explore</p>
          <ul className="space-y-3 text-sm font-light">
            <li><Link to="/shop" className="hover:text-sun">Shop All</Link></li>
            <li><Link to="/shop?cat=hay" className="hover:text-sun">Hay</Link></li>
            <li><Link to="/shop?cat=pellets" className="hover:text-sun">Pellets</Link></li>
            <li><Link to="/shop?cat=specialty" className="hover:text-sun">Specialty</Link></li>
            <li><Link to="/shop?cat=bedding" className="hover:text-sun">Bedding</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="tag-line mb-5" style={{ color: "#F3BE4A" }}>Reach Us</p>
          <ul className="space-y-4 text-sm font-light">
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-0.5 text-sun flex-shrink-0" />
              <a href={`mailto:${BRAND_EMAIL}`} className="hover:text-sun" data-testid="footer-email">{BRAND_EMAIL}</a>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="w-4 h-4 mt-0.5 text-sun flex-shrink-0" />
              <a href={`tel:${BRAND_PHONE}`} className="hover:text-sun" data-testid="footer-phone">{BRAND_PHONE}</a>
            </li>
            <li className="flex items-start gap-3" data-testid="footer-address">
              <MapPin className="w-4 h-4 mt-0.5 text-sun flex-shrink-0" />
              <address className="not-italic leading-relaxed">
                RSA Overseas Pvt Ltd<br />
                {formatAddressLine()}
              </address>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="w-4 h-4 mt-0.5 text-sun flex-shrink-0" />
              <span>
                Warehouse: Mon–Sat, 10:00–19:00 IST
                <br />
                <span className="text-cream/60">Online store: 24 × 7</span>
              </span>
            </li>
            <li className="flex items-start gap-3" data-testid="footer-gstin">
              <FileText className="w-4 h-4 mt-0.5 text-sun flex-shrink-0" />
              <span>GSTIN: {ORG_CONTACT.gstin}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-cream/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-cream/60 font-light">
          <p>&copy; {new Date().getFullYear()} Hopsy · RSA Overseas Pvt Ltd. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy-policy" className="hover:text-sun transition" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-sun transition" data-testid="footer-terms">Terms</Link>
            <p className="font-serif italic text-sm text-cream/70 hidden md:block">Crafted for happy, healthy little ones.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
